import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Button, Paper, IconButton, MobileStepper, CircularProgress, Container as MuiContainer } from '@mui/material';
import { styled } from '@mui/material/styles';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useNavigate } from 'react-router-dom';
import { Base_URL } from '../../Client/apiURL';
import { processAudioUpload } from '../../pte-mock-test/utils/audioProcessing';
import IELTSBottomNavigation from './IELTSBottomNavigation';
import IELTSHeader from './IELTSHeader';
import IELTSBandScore from './IELTSBandScore';
import IELTSDetailedScoring from './IELTSDetailedScoring';
import SubscriptionAiPromptDialog from './SubscriptionAiPromptDialog';
import InstructionCard from './InstructionCard';
import EndModal from '../MockTest/Common/EndModal';
import { playStartBeep, playEndBeep } from '../../pte-mock-test/utils/audio';
import { hasAIScore, shouldShowScore, getAIScoring } from './utils/scoreHelpers';
import { processSpeakingQuestionHtml } from './utils/speakingQuestionProcessor';
import { formatTime } from './utils/timeHelpers';
import { getWaitingTime, getPreInstructions, shouldShowPrePartInstructions } from './utils/speakingHelpers';

// IELTS Speaking-specific messages for EndModal
const IELTS_SPEAKING_SENTENCES = [
  "Processing your speaking responses...",
  "Analyzing pronunciation...",
  "Evaluating fluency and coherence...",
  "Scoring lexical resource...",
  "Finalizing your speaking test results..."
];

const QuestionBox = styled(Paper)(({ theme }) => ({
  padding: '20px',
  marginBottom: '16px',
  borderRadius: '12px',
  background: '#fff',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'stretch',
  maxWidth: '100%',
  overflow: 'hidden',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },
}));

const PartHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#F5F5F5',
  padding: '12px 20px',
  borderRadius: '8px 8px 0 0',
  borderBottom: '1px solid #E0E0E0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontWeight: 600,
  color: '#2D3748',
  textTransform: 'uppercase',
  fontSize: '14px',
  letterSpacing: '0.5px',
}));

const ThinkTimerButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#996CFE',
  color: 'white',
  borderRadius: '6px',
  padding: '6px 16px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  minWidth: '140px',
  '&:hover': {
    backgroundColor: '#7C4DFF',
  },
}));

const QuestionLabel = styled(Typography)(({ theme }) => ({
  color: '#996CFE',
  fontWeight: 700,
  fontSize: '16px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  marginBottom: '16px',
}));

const QuestionText = styled(Box)(({ theme }) => ({
  color: '#2D3748',
  marginBottom: '32px',
  lineHeight: 1.5,
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  maxWidth: '100%',
  overflow: 'hidden',
  '& p': {
    marginBottom: '12px',
    lineHeight: 1.6,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    maxWidth: '100%',
  },
  '& ul, & ol': {
    marginLeft: '24px',
    marginBottom: '12px',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    maxWidth: '100%',
  },
  '& li': {
    marginBottom: '8px',
    lineHeight: 1.6,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  '& strong, & b': {
    fontWeight: 700,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  '& em, & i': {
    fontStyle: 'italic',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  '& *': {
    maxWidth: '100%',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: '24px',
  },
}));

const MicrophoneButton = styled(IconButton)(({ theme, recording, thinking, playing }) => ({
  width: '80px',
  height: '80px',
  backgroundColor: recording ? '#E53935' : thinking ? '#996CFE' : playing ? '#37965E' : '#fff',
  border: `2px solid ${recording ? '#E53935' : thinking ? '#996CFE' : playing ? '#37965E' : '#E0E0E0'}`,
  boxShadow: recording || thinking || playing
    ? '0 2px 12px rgba(153, 108, 254, 0.25)' 
    : '0 2px 8px rgba(0,0,0,0.08)',
  margin: '16px auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: recording ? '#C62828' : thinking ? '#7C4DFF' : playing ? '#2A7349' : '#F5F5F5',
    transform: 'scale(1.05)',
  },
  '&:disabled': {
    backgroundColor: thinking ? '#996CFE' : '#fff',
    border: `2px solid ${thinking ? '#996CFE' : '#E0E0E0'}`,
    opacity: thinking ? 1 : 0.6,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '36px',
    color: recording || thinking || playing ? 'white' : '#996CFE',
  },
  [theme.breakpoints.down('sm')]: {
    width: '70px',
    height: '70px',
    '& .MuiSvgIcon-root': {
      fontSize: '32px',
    },
  },
}));

async function uploadAudioFile(audioBlob) {
  return await processAudioUpload(audioBlob);
}

const formatClockTime = (totalSeconds) => {
  if (totalSeconds === 'Unlimited') return 'Unlimited';
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const SpeakingTestUI = ({ sections = [], onComplete, testId, userId, groupId, fullTestAttemptId, showSaveExit = false, timeLimit = 15, mode = 'test', apiResult: apiResultProp = null, onSubmitStart, onFullTestPause, requiresSubscriptionForAiScoring = false, preferManualScore = false }) => {
  const navigate = useNavigate();
  const [apiResult, setApiResult] = useState(apiResultProp);
  
  useEffect(() => {
    if (apiResultProp) setApiResult(apiResultProp);
  }, [apiResultProp]);
  
  const hasResults = apiResult && (shouldShowScore(apiResult) || hasAIScore(apiResult));
  const isReview = mode === 'review' || hasResults;
  const isReviewModeFromProps = mode === 'review';
  const isTestMode = mode === 'test';
  const isPracticeMode = mode === 'practice';
  
  const allQuestions = useMemo(() => {
    return sections.flatMap((section, sectionIdx) => {
      const sectionQuestions = section.questions || [];
      return sectionQuestions.map((question, qIdx) => ({
        id: question.id,
        sectionIdx,
        questionIdx: qIdx,
        part_name: section.part_name,
        examiner_notes: section.examiner_notes,
        passage_text: section.passage_text,
        question_html: question.question_html,
        cue_card_text: question.cue_card_text,
        statement: question.statement,
        order_id: question.order_id || (qIdx + 1),
        question_type: question.question_type,
      }));
    });
  }, [sections]);
  
  const [currentIdx, setCurrentIdx] = useState(0);
  const [recording, setRecording] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [audioURLs, setAudioURLs] = useState({});
  const [audioBlobs, setAudioBlobs] = useState({});
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);
  const submitInFlightRef = useRef(false);
  const finalSubmitCompletedRef = useRef(false);
  const [timer, setTimer] = useState({
    minutes: isPracticeMode ? 0 : (timeLimit === 'Unlimited' ? 'Unlimited' : (timeLimit || 15)),
    seconds: 0
  });
  const [isTimerRunning, setIsTimerRunning] = useState(true);
  const [thinkTime, setThinkTime] = useState(5); // 5 seconds to think
  const [isThinking, setIsThinking] = useState(false);
  const [testStartTime] = useState(() => new Date().toISOString().slice(0, 19).replace('T', ' '));
  const [testEndTime, setTestEndTime] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [endModalOpen, setEndModalOpen] = useState(false);
  const [subscriptionPromptOpen, setSubscriptionPromptOpen] = useState(false);
  
  const [showingInstructions, setShowingInstructions] = useState(false);
  const [currentInstructions, setCurrentInstructions] = useState([]);
  const [currentInstructionIndex, setCurrentInstructionIndex] = useState(0);
  const [pendingPartIndex, setPendingPartIndex] = useState(null);
  const [instructionsShownOnMount, setInstructionsShownOnMount] = useState(false);
  const [visitedParts, setVisitedParts] = useState([1]);
  
  useEffect(() => {
    if (!instructionsShownOnMount && !isReview && shouldShowPrePartInstructions() && allQuestions.length > 0) {
      const firstPart = allQuestions[0]?.sectionIdx !== undefined ? allQuestions[0].sectionIdx + 1 : 1;
      const instructions = getPreInstructions(firstPart);
      if (instructions && instructions.length > 0) {
        setCurrentInstructions(instructions);
        setCurrentInstructionIndex(0);
        setPendingPartIndex(firstPart);
        setShowingInstructions(true);
        setInstructionsShownOnMount(true);
      } else {
        setInstructionsShownOnMount(true);
      }
    } else if (allQuestions.length > 0 && !instructionsShownOnMount) {
      setInstructionsShownOnMount(true);
    }
  }, [allQuestions, isReview, instructionsShownOnMount]);
  
  // Auto-dismiss info messages after 5 seconds (except errors)
  useEffect(() => {
    if (infoMessage && infoMessage.severity !== 'error') {
      const timer = setTimeout(() => {
        setInfoMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [infoMessage]);
  
  const questionHtmlRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const skipThinkingRef = useRef(false);
  
  // Stop recordings and timer when results are available
  useEffect(() => {
    if (hasResults) {
      // Stop any active recording
      if (recording) {
        setRecording(false);
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          try {
            mediaRecorderRef.current.stop();
          } catch (e) {
            console.error('Error stopping recorder:', e);
          }
        }
      }
      // Stop media stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      // Stop timer
      setIsTimerRunning(false);
      // Stop any playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setPlaying(false);
      }
      // Clear thinking state
      setIsThinking(false);
      // Close modal when results are available
      if (endModalOpen) {
        setEndModalOpen(false);
      }
    }
  }, [hasResults, recording, endModalOpen]);

  const currentQuestion = useMemo(() => allQuestions[currentIdx], [allQuestions, currentIdx]);

  const getFormattedPartName = () => {
    if (!currentQuestion) return '';
    const partName = currentQuestion.part_name || '';
    const examinerNotes = currentQuestion.examiner_notes || '';
    
    if (partName && examinerNotes) {
      if (partName.match(/Part\s*\d+/i) && examinerNotes.match(/Part\s*\d+/i)) {
        return examinerNotes;
      }
      return `${partName} - ${examinerNotes}`;
    }
    return examinerNotes || partName;
  };

  const uploadAudioInBackground = useCallback(async (blob, questionIdx) => {
    if (!blob || blob.size === 0) return;
    
    try {
      const url = await uploadAudioFile(blob);
      if (url) {
        setAudioURLs(prev => ({ ...prev, [questionIdx]: url }));
      }
    } catch (err) {
    }
  }, []);

  const handleStartRecording = useCallback(async () => {
    if (isReview) return; // Do not allow recording in review mode
    if (!navigator.mediaDevices?.getUserMedia) {
      setInfoMessage({
        text: 'Audio recording is not supported in this browser.',
        severity: 'error'
      });
      return;
    }
    
    if (recording) return;
    
    // Clear thinking timer if it's running
    if (thinkTimerIntervalRef.current) {
      clearInterval(thinkTimerIntervalRef.current);
      thinkTimerIntervalRef.current = null;
    }
    setIsThinking(false);
    setThinkTime(0);
    setLoading(true);
    
    try {
      await playStartBeep();
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        if (blob.size > 0) {
          const questionIdx = currentIdx;
          setAudioBlobs(prev => ({ ...prev, [questionIdx]: blob }));
          uploadAudioInBackground(blob, questionIdx).catch(() => {});
        }
        
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        
        setRecording(false);
        setLoading(false);
      };
      
      mediaRecorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      setInfoMessage({
        text: 'Could not start audio recording.',
        severity: 'error'
      });
      setLoading(false);
      setRecording(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }
  }, [recording, currentIdx, uploadAudioInBackground]);

  const thinkTimeRef = useRef(0);
  const thinkTimerIntervalRef = useRef(null);
  const currentQuestionIdRef = useRef(null);

  // Start thinking time countdown when question changes (only in timed exam/test mode).
  useEffect(() => {
    if (isReview) return;
    if (isPracticeMode) {
      if (thinkTimerIntervalRef.current) {
        clearInterval(thinkTimerIntervalRef.current);
        thinkTimerIntervalRef.current = null;
      }
      setIsThinking(false);
      setThinkTime(0);
      return;
    }
    
    // Don't start thinking timer if instructions are showing
    if (showingInstructions) {
      // Clear any running thinking timer when instructions show
      if (thinkTimerIntervalRef.current) {
        clearInterval(thinkTimerIntervalRef.current);
        thinkTimerIntervalRef.current = null;
      }
      setIsThinking(false);
      // Reset currentQuestionIdRef so timer can start when instructions are dismissed
      currentQuestionIdRef.current = null;
      return;
    }
    
    const currentQ = allQuestions[currentIdx];
    if (!currentQ) return;
    
    // Clear any existing interval first when question changes
    if (thinkTimerIntervalRef.current) {
      clearInterval(thinkTimerIntervalRef.current);
      thinkTimerIntervalRef.current = null;
    }
    
    // Only run if question actually changed
    if (currentQuestionIdRef.current === currentQ.id) {
      return;
    }
    
    // Update the ref to track the current question
    currentQuestionIdRef.current = currentQ.id;
    
    const hasAnswer = audioURLs[currentIdx] || audioBlobs[currentIdx];
    const partNumber = currentQ.sectionIdx + 1;
    const waitingTime = getWaitingTime(partNumber);
    
    // Reset thinking state
    setIsThinking(false);
    setThinkTime(waitingTime);
    thinkTimeRef.current = waitingTime;
    
    if (skipThinkingRef.current) {
      skipThinkingRef.current = false;
      return;
    }
    
    if (!hasAnswer && !recording) {
      // Use a small delay to ensure state is settled
      const questionIdToCheck = currentQ.id;
      const timer = setTimeout(() => {
        // Double-check we're still on the same question and not recording
        const currentQNow = allQuestions[currentIdx];
        if (currentQNow && currentQuestionIdRef.current === questionIdToCheck && currentQNow.id === questionIdToCheck && !recording && !audioURLs[currentIdx] && !audioBlobs[currentIdx]) {
          setIsThinking(true);
          
          // Start countdown using interval
          thinkTimerIntervalRef.current = setInterval(() => {
            thinkTimeRef.current -= 1;
            
            if (thinkTimeRef.current <= 0) {
              clearInterval(thinkTimerIntervalRef.current);
              thinkTimerIntervalRef.current = null;
              setIsThinking(false);
              setThinkTime(0);
              handleStartRecording();
            } else {
              // Update UI only every second to reduce re-renders
              setThinkTime(thinkTimeRef.current);
            }
          }, 1000);
        }
      }, 200);
      
      return () => {
        clearTimeout(timer);
        if (thinkTimerIntervalRef.current) {
          clearInterval(thinkTimerIntervalRef.current);
          thinkTimerIntervalRef.current = null;
        }
      };
    }
  }, [currentIdx, audioURLs, audioBlobs, recording, isReview, isPracticeMode, handleStartRecording, allQuestions, showingInstructions]);

  useEffect(() => {
    if (questionHtmlRef.current) {
      const indicators = questionHtmlRef.current.querySelectorAll('.correct-answer-indicator');
      indicators.forEach(el => el.style.display = 'none');
      function removeNoAnswerText(node) {
        if (node.nodeType === 3 && /Answer: No Answer/.test(node.textContent)) {
          node.textContent = '';
        } else if (node.nodeType === 1) {
          Array.from(node.childNodes).forEach(removeNoAnswerText);
        }
      }
      removeNoAnswerText(questionHtmlRef.current);
    }
  }, [currentQuestion]);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleStopRecording = useCallback(async () => {
    if (!recording || !mediaRecorderRef.current) return;
    
    try {
      await playEndBeep();
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    } catch (err) {
      setRecording(false);
      setLoading(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }
  }, [recording]);

  const handlePlayAudio = useCallback(() => {
    if (playing) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlaying(false);
      return;
    }
    
    const url = audioURLs[currentIdx];
    const blob = audioBlobs[currentIdx];
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    if (url) {
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onplay = () => setPlaying(true);
      audio.onended = () => {
        setPlaying(false);
        audioRef.current = null;
      };
      audio.onpause = () => {
        setPlaying(false);
        audioRef.current = null;
      };
      audio.play().catch(() => {
        setPlaying(false);
        audioRef.current = null;
      });
    } else if (blob) {
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.onplay = () => setPlaying(true);
      audio.onended = () => {
        setPlaying(false);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };
      audio.onpause = () => {
        setPlaying(false);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };
      audio.play().catch(() => {
        setPlaying(false);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      });
    }
  }, [currentIdx, audioURLs, audioBlobs, playing]);

  const handleNext = useCallback(async () => {
    if (recording) {
      await handleStopRecording();
    }
    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlaying(false);
    }
    
    const nextIdx = Math.min(currentIdx + 1, allQuestions.length - 1);
    const currentPart = currentQuestion?.sectionIdx;
    const nextQuestion = allQuestions[nextIdx];
    const nextPart = nextQuestion?.sectionIdx;
    
    // Check if we're transitioning to a new part
    if (currentPart !== nextPart && nextPart !== undefined) {
      const nextPartNumber = nextPart + 1;
      
      // Mark the new part as visited
      setVisitedParts(prev => {
        if (!prev.includes(nextPartNumber)) {
          return [...prev, nextPartNumber];
        }
        return prev;
      });
      
      // Show instructions if configured
      if (shouldShowPrePartInstructions()) {
        const instructions = getPreInstructions(nextPartNumber);
        if (instructions && instructions.length > 0) {
          setCurrentInstructions(instructions);
          setCurrentInstructionIndex(0);
          setPendingPartIndex(nextPartNumber);
          setShowingInstructions(true);
          return;
        }
      }
    }
    
    setCurrentIdx(nextIdx);
  }, [recording, handleStopRecording, playing, currentIdx, allQuestions, currentQuestion]);
  
  const handleBack = useCallback(async () => {
    if (recording) {
      await handleStopRecording();
    }
    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlaying(false);
    }
    setCurrentIdx(idx => Math.max(idx - 1, 0));
  }, [recording, handleStopRecording, playing]);

  const ensureAllRecordingsUploaded = useCallback(async () => {
    const pendingUploads = [];
    
    allQuestions.forEach((q, idx) => {
      const blob = audioBlobs[idx];
      const url = audioURLs[idx];
      
      if (blob && !url) {
        pendingUploads.push(uploadAudioInBackground(blob, idx));
      }
    });
    
    if (pendingUploads.length > 0) {
      await Promise.allSettled(pendingUploads);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }, [allQuestions, audioBlobs, audioURLs, uploadAudioInBackground]);

  const submitTest = useCallback(async (isSaveAndExit = false) => {
    if (submitInFlightRef.current || (!isSaveAndExit && finalSubmitCompletedRef.current)) {
      return { success: false, duplicate: true };
    }

    submitInFlightRef.current = true;
    if (!isSaveAndExit) {
      finalSubmitCompletedRef.current = true;
    }

    setLoading(true);
    
    // Call onSubmitStart immediately if provided (for full test navigation) - only on actual submit, not save&exit
    if (onSubmitStart && !isSaveAndExit) {
      onSubmitStart();
    }
    
    // Show EndModal for practice/simulation mode (not full test mode)
    if (!isTestMode && !isSaveAndExit) {
      setEndModalOpen(true);
    }
    
    // Stop recording if active
    if (recording) {
      await handleStopRecording();
    }
    
    // Ensure all recordings are uploaded before submission
    await ensureAllRecordingsUploaded();
    
    // Set end time when submitting
    const endTime = new Date().toISOString().slice(0, 19).replace('T', ' ');
    setTestEndTime(endTime);
    
    try {
      // For each part, gather questions with their audio URLs
      const partPayloads = sections.map((section, idx) => {
        // Find all questions for this part
        const partQuestions = allQuestions.filter(q => q.sectionIdx === idx);
        
        // Map each question to its audio URL and text
        const questionsData = partQuestions.map((q) => {
          const globalIdx = allQuestions.findIndex(gq => gq.id === q.id);
          return {
            questionId: q.id,
            questionText: q.question_html || '',
            audioUrl: audioURLs[globalIdx] || null
          };
        });
        
        return {
          [`part${idx + 1}`]: {
            partId: section.id,
            questions: questionsData,
            timeTaken: 0
          }
        };
      });
      
      const payload = {
        testId,
        start_time: testStartTime,
        end_time: endTime,
        status: isSaveAndExit ? 'in_progress' : 'pending_evaluation',
        ...(groupId ? { groupId } : {}),
        ...(groupId && fullTestAttemptId ? { fullTestAttemptId } : {}),
        ...(groupId ? { is_speaking_optional: localStorage.getItem('is_speaking_optional') === 'true' } : {}),
        ...Object.assign({}, ...partPayloads)
      };
      
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/saveSpeakingTestAttempt`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.error || 'Save failed');
      
      // Close modal if results are available
      if (!isSaveAndExit && (hasAIScore(data) || shouldShowScore(data))) {
        setEndModalOpen(false);
      }
      
      return { success: true, data };
    } catch (err) {
      if (!isSaveAndExit) {
        finalSubmitCompletedRef.current = false;
      }
      setEndModalOpen(false);
      return { success: false, error: err.message };
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
    }
  }, [recording, handleStopRecording, ensureAllRecordingsUploaded, sections, allQuestions, audioURLs, testStartTime, testId, groupId, fullTestAttemptId, isTestMode, onSubmitStart]);

  const handleSubmit = useCallback(async () => {
    if (requiresSubscriptionForAiScoring && !isReviewModeFromProps) {
      if (recording) {
        await handleStopRecording();
      }
      setSubscriptionPromptOpen(true);
      return;
    }
    const result = await submitTest(false);
    if (result.duplicate) {
      return;
    }

    if (result.success) {
      // Check response status to show appropriate message
      const status = result.data?.response?.status;
      const hasAIScore = result.data?.response?.analytics?.ai_scoring && status === 'completed';
      
      if (hasAIScore) {
        setInfoMessage({
          text: 'Your test has been submitted and scored successfully. View your results below.',
          severity: 'success'
        });
      } else if (status === 'pending_evaluation') {
        setInfoMessage({
          text: 'Your test has been submitted. Results are pending evaluation.',
          severity: 'info'
        });
      } else {
        setInfoMessage({
          text: 'Your test has been submitted successfully.',
          severity: 'success'
        });
      }
      setApiResult(result.data);
      if (onComplete) onComplete(result.data);
    } else {
      setInfoMessage({
        text: 'Submission failed: ' + result.error,
        severity: 'error'
      });
    }
  }, [requiresSubscriptionForAiScoring, isReviewModeFromProps, recording, handleStopRecording, submitTest, onComplete]);

  useEffect(() => {
    let interval;
    // Check if instructions are showing - pause timer if instructions are active
    // (instructions are shown for first-time parts, so we pause to let user read)
    const shouldPauseTimer = showingInstructions;
    
    if (isTimerRunning && isPracticeMode && !shouldPauseTimer) {
      interval = setInterval(() => {
        setTimer(prevTimer => {
          if (prevTimer.seconds === 59) {
            return { minutes: prevTimer.minutes + 1, seconds: 0 };
          }
          return { ...prevTimer, seconds: prevTimer.seconds + 1 };
        });
      }, 1000);
    } else if (isTimerRunning && timer.minutes !== 'Unlimited' && !shouldPauseTimer) {
      interval = setInterval(() => {
        setTimer(prevTimer => {
          if (prevTimer.minutes === 0 && prevTimer.seconds === 0) {
            clearInterval(interval);
            setIsTimerRunning(false);
            handleSubmit();
            return prevTimer;
          }
          if (prevTimer.seconds === 0) {
            return { minutes: prevTimer.minutes - 1, seconds: 59 };
          } else {
            return { ...prevTimer, seconds: prevTimer.seconds - 1 };
          }
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerRunning, timer.minutes, handleSubmit, showingInstructions, isPracticeMode]);

  const handleSaveAndExit = async () => {
    const result = await submitTest(true); // true = save&exit, so in_progress
    if (result.duplicate) {
      return;
    }

    if (result.success) {
      setInfoMessage({
        text: 'Progress saved! You can resume this test later.',
        severity: 'success'
      });
      setTimeout(() => {
        navigate('/ielts-dashboard');
      }, 1500);
    } else {
      setInfoMessage({
        text: 'Failed to save progress: ' + result.error,
        severity: 'error'
      });
    }
  };

  const getSectionQuestionNumbers = (sectionIdx) => {
    const sectionQuestions = allQuestions.filter(q => q.sectionIdx === sectionIdx);
    return sectionQuestions.map((q) => {
      const globalIdx = allQuestions.findIndex(aq => aq.id === q.id);
      return String(q.order_id || (globalIdx + 1));
    });
  };

  const getAnsweredQuestions = () => {
    const answered = {};
    sections.forEach((section, sectionIdx) => {
      const sectionQuestions = allQuestions.filter(q => q.sectionIdx === sectionIdx);
      sectionQuestions.forEach((q) => {
        const globalIdx = allQuestions.findIndex(aq => aq.id === q.id);
        const questionNum = String(q.order_id || (globalIdx + 1));
        const hasAudio = !!(audioBlobs[globalIdx] || audioURLs[globalIdx]);
        if (hasAudio) {
          answered[questionNum] = true;
        }
      });
    });
    return answered;
  };

  const isQuestionAnswered = useCallback((sectionIdx, questionNumber) => {
    const sectionQuestions = allQuestions.filter(q => q.sectionIdx === sectionIdx);
    const question = sectionQuestions.find((q) => {
      const globalIdx = allQuestions.findIndex(aq => aq.id === q.id);
      const questionNum = String(q.order_id || (globalIdx + 1));
      return questionNum === questionNumber;
    });
    
    if (!question) return false;
    
    const globalIdx = allQuestions.findIndex(aq => aq.id === question.id);
    return !!(audioBlobs[globalIdx] || audioURLs[globalIdx]);
  }, [allQuestions, audioBlobs, audioURLs]);

  const handleQuestionClick = useCallback((questionNumber, sectionIdx) => {
    if (sectionIdx === undefined || sectionIdx === null) {
      const questionIdx = allQuestions.findIndex((q, i) => {
        const num = String(q.order_id || (i + 1));
        return num === questionNumber;
      });
      if (questionIdx !== -1) {
        setCurrentIdx(questionIdx);
      }
      return;
    }
    
    const sectionQuestions = allQuestions.filter(q => q.sectionIdx === sectionIdx);
    const question = sectionQuestions.find((q) => {
      const globalIdx = allQuestions.findIndex(aq => aq.id === q.id);
      const questionNum = String(q.order_id || (globalIdx + 1));
      return questionNum === questionNumber;
    });
    
    if (question) {
      const globalIdx = allQuestions.findIndex(aq => aq.id === question.id);
      setCurrentIdx(globalIdx);
    }
  }, [allQuestions]);

  const handleInstructionNext = useCallback(() => {
    if (currentInstructionIndex < currentInstructions.length - 1) {
      setCurrentInstructionIndex(prev => prev + 1);
    } else {
      setShowingInstructions(false);
      setCurrentInstructions([]);
      setCurrentInstructionIndex(0);
      
      if (pendingPartIndex !== null) {
        const sectionIdx = pendingPartIndex - 1;
        const questionInfo = allQuestions
          .map((q, i) => {
            if (q.sectionIdx === sectionIdx) {
              return { idx: i, realNum: q.order_id || (i + 1) };
            }
            return null;
          })
          .filter(i => i !== null);
        
        if (questionInfo.length > 0) {
          setCurrentIdx(questionInfo[0].idx);
        }
        setPendingPartIndex(null);
      } else if (allQuestions.length > 0) {
        setCurrentIdx(0);
      }
    }
  }, [currentInstructionIndex, currentInstructions.length, pendingPartIndex, allQuestions]);

  const handleInstructionPrevious = useCallback(() => {
    if (currentInstructionIndex > 0) {
      setCurrentInstructionIndex(prev => prev - 1);
    }
  }, [currentInstructionIndex]);

  const handlePartChange = useCallback(async (partNumber) => {
    if (recording) {
      return;
    }
    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlaying(false);
    }
    
    const actualPartNumber = typeof partNumber === 'number' ? partNumber : partNumber;
    const isAlreadyVisited = visitedParts.includes(actualPartNumber);
    
    // Mark part as visited
    setVisitedParts(prev => {
      if (!prev.includes(actualPartNumber)) {
        return [...prev, actualPartNumber];
      }
      return prev;
    });
    
    // Skip instructions if part is already visited (user is navigating back)
    if (!isAlreadyVisited && shouldShowPrePartInstructions()) {
      const instructions = getPreInstructions(actualPartNumber);
      if (instructions && instructions.length > 0) {
        setCurrentInstructions(instructions);
        setCurrentInstructionIndex(0);
        setPendingPartIndex(actualPartNumber);
        setShowingInstructions(true);
        return;
      }
    }
    
    // Go directly to part questions
    const sectionIdx = typeof partNumber === 'number' ? partNumber - 1 : partNumber;
    const questionInfo = allQuestions
      .map((q, i) => {
        if (q.sectionIdx === sectionIdx) {
          return { idx: i, realNum: q.order_id || (i + 1) };
        }
        return null;
      })
      .filter(i => i !== null);
    
    if (questionInfo.length > 0) {
      setCurrentIdx(questionInfo[0].idx);
    }
  }, [recording, allQuestions, playing, visitedParts]);

  // ----- Review/Result mode handling -----
  const [currentPart, setCurrentPart] = useState(1);

  if (isReview) {
    const analytics = apiResult?.response?.analytics || {};
    const currentSection = sections[currentPart - 1];
    const currentSectionQuestions = currentSection?.questions || [];

    const partKey = `part${currentSection?.order_id || currentPart}`;
    const partData = analytics[partKey];
    const audioMap = {};
    if (partData?.questions && Array.isArray(partData.questions)) {
      partData.questions.forEach(q => {
        if (q.questionId) audioMap[q.questionId] = q.audioUrl;
      });
    } else if (partData?.audioUrl && Array.isArray(partData.audioUrl)) {
      partData.audioUrl.forEach((url, urlIdx) => {
        const question = currentSectionQuestions[urlIdx];
        if (question?.id) audioMap[question.id] = url;
      });
    }

    const getSectionQuestionNumbersReview = (sectionIdx) => {
      if (!sections[sectionIdx]) return [];
      const sectionQuestions = sections[sectionIdx].questions || [];
      return sectionQuestions.map((q, idx) => String(q.order_id || (idx + 1)));
    };

    const getAnsweredQuestionsReview = () => {
      const answered = {};
      sections.forEach((section, sectionIdx) => {
        const sectionQuestions = section.questions || [];
        sectionQuestions.forEach((q, qIdx) => {
          const questionNum = String(q.order_id || (qIdx + 1));
          const pKey = `part${section.order_id || (sectionIdx + 1)}`;
          const pData = analytics[pKey];
          let hasAudio = false;
          if (pData?.questions && Array.isArray(pData.questions)) {
            hasAudio = pData.questions.some(pq => pq.questionId === q.id && pq.audioUrl);
          } else if (pData?.audioUrl && Array.isArray(pData.audioUrl)) {
            hasAudio = !!pData.audioUrl[qIdx];
          }
          if (hasAudio) answered[questionNum] = true;
        });
      });
      return answered;
    };

    const isQuestionAnsweredReview = (sectionIdx, questionNumber) => {
      const section = sections[sectionIdx];
      if (!section) return false;
      const sectionQuestions = section.questions || [];
      const question = sectionQuestions.find((q, idx) => String(q.order_id || (idx + 1)) === questionNumber);
      if (!question) return false;
      const pKey = `part${section.order_id || (sectionIdx + 1)}`;
      const pData = analytics[pKey];
      if (!pData) return false;
      if (pData?.questions && Array.isArray(pData.questions)) {
        return pData.questions.some(pq => pq.questionId === question.id && pq.audioUrl);
      } else if (pData?.audioUrl && Array.isArray(pData.audioUrl)) {
        const qIdx = sectionQuestions.findIndex(q => q.id === question.id);
        return !!pData.audioUrl[qIdx];
      }
      return false;
    };

    const handlePartChangeReview = (partNumber) => {
      const partNum = typeof partNumber === 'number' ? partNumber : partNumber;
      setCurrentPart(partNum);
    };

    const handleQuestionClickReview = (questionNumber, sectionIdx) => {
      if (sectionIdx !== undefined && sectionIdx !== null && sectionIdx !== currentPart - 1) {
        setCurrentPart(sectionIdx + 1);
        setTimeout(() => {
          const element = document.getElementById(`question-${questionNumber}`);
          if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      } else {
        const element = document.getElementById(`question-${questionNumber}`);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    // Use helper functions to check score availability (handles 0 correctly)
    const scoreAvailable = shouldShowScore(apiResult);
    const aiScoring = getAIScoring(apiResult);

    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        height: 'auto',
        overflow: 'visible',
        overflowY: 'auto',
        background: '#F5F6FA' 
      }}>
        {/* Header - Only show when results are from submission, not from review mode (test details page) */}
        {!isReviewModeFromProps && (
          <IELTSHeader
            timerText={`Test Completed - ${timer.minutes !== 'Unlimited' ? formatTime(timer.minutes * 60 + timer.seconds) : 'Unlimited'}`}
            showReview={false}
            showSaveExit={false}
            showSubmit={true}
            apiResult={apiResult}
            onSubmitNavigate={apiResult?.response?.attemptId ? `/ielts-test-details?type=individual&attemptId=${apiResult.response.attemptId}` : null}
            submitButtonText="View Results"
          />
        )}
        
        <Box sx={{ width: '100%', flex: 1 }}>
          {scoreAvailable && <IELTSBandScore apiResult={apiResult} showCorrectIncorrect={false} showOnlyWhenScoreAvailable={true} preferManualScore={preferManualScore} />}
          {hasAIScore(apiResult) && <IELTSDetailedScoring aiScoring={aiScoring} testType="Speaking" />}

        <IELTSBottomNavigation
          parts={sections.map((section, idx) => ({ part: idx + 1, sectionIdx: idx, part_name: section.part_name || `Part ${idx + 1}` }))}
          currentPart={currentPart}
          onPartChange={handlePartChangeReview}
          getQuestionNumbers={getSectionQuestionNumbersReview}
          allAnswers={getAnsweredQuestionsReview()}
          isQuestionAnswered={isQuestionAnsweredReview}
          skippedQuestions={[]}
          onQuestionClick={handleQuestionClickReview}
          getPartName={(part) => part.part_name || `Part ${part.part || part}`}
          fixed={false}
        />

        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', overflow: 'visible', position: 'relative' }}>
          <Box sx={{ backgroundColor: '#F5F5F5', padding: '12px 20px', borderRadius: '8px 8px 0 0', borderBottom: '1px solid #E0E0E0', marginBottom: 0, flexShrink: 0 }}>
            {currentSection?.examiner_notes && <Typography variant="h6" color="text.primary" sx={{ mt: 0.5, fontWeight: 700 }}>{currentSection.examiner_notes}</Typography>}
          </Box>

          <Box sx={{ width: '100%', overflowY: 'visible', overflowX: 'hidden', padding: '20px 0' }}>
            {currentSectionQuestions.map((question, qIdx) => {
              const audioUrl = audioMap[question.id];
              const questionNum = String(question.order_id || (qIdx + 1));
              return (
                <QuestionBox key={question.id || qIdx} id={`question-${questionNum}`}>
                  <Typography variant="h6" sx={{ color: '#996CFE', fontWeight: 700, mb: 2 }}>Question {questionNum}</Typography>
                  {question.cue_card_text && (
                    <Box sx={{ mb: 3, p: 3, backgroundColor: '#FFF9E6', borderRadius: 2, border: '2px solid #FFD700', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '100%', overflow: 'hidden' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#996CFE' }}>Cue Card:</Typography>
                      <Typography variant="body1" sx={{ color: '#2D3748', lineHeight: 1.6, wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '100%' }}>{question.cue_card_text}</Typography>
                    </Box>
                  )}
                  {question.statement && (<Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic', color: '#4A5568', fontSize: '18px', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '100%' }}>{question.statement}</Typography>)}
                  <Typography variant="body1" sx={{ mb: 3, color: '#2D3748', lineHeight: 1.5, wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '100%', overflow: 'hidden' }}>
                    <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '100%' }} dangerouslySetInnerHTML={{ __html: processSpeakingQuestionHtml(question.question_html || 'No question') }} />
                  </Typography>
                  <Box sx={{ mt: 3, pt: 3, borderTop: '1px dashed #E0E0E0' }}>
                    {audioUrl ? (
                      <>
                        <Typography variant="body2" sx={{ mb: 2, color: '#555', fontWeight: 500 }}>Your Recording:</Typography>
                        <audio controls src={audioUrl} style={{ width: '100%', maxWidth: 500 }} />
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>Not attempted</Typography>
                    )}
                  </Box>
                </QuestionBox>
              );
            })}
          </Box>
        </Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes audioPulse {
            0%, 100% { transform: scaleY(0.4); opacity: 0.7; }
            50% { transform: scaleY(1); opacity: 1; }
          }
        `}
      </style>
      {/* EndModal for practice/simulation mode */}
      {!isTestMode && (
        <EndModal 
          open={endModalOpen} 
          onClose={() => setEndModalOpen(false)} 
          sentences={IELTS_SPEAKING_SENTENCES} 
          intervalMs={3000}
          testTitle="IELTS Speaking Mock Test"
          navigateTo="/ielts-results"
        />
      )}
      {showingInstructions && currentInstructions.length > 0 ? (
        <InstructionCard
          title={currentInstructions[currentInstructionIndex].title}
          content={currentInstructions[currentInstructionIndex].content}
          type={currentInstructions[currentInstructionIndex].type}
          showPrevious={currentInstructionIndex > 0}
          showNext={true}
          onPrevious={handleInstructionPrevious}
          onNext={handleInstructionNext}
          nextButtonText={
            currentInstructionIndex === currentInstructions.length - 1
              ? `Continue to Part ${pendingPartIndex}`
              : 'Next'
          }
        />
      ) : (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: hasResults ? 'auto' : '100vh', 
          height: hasResults ? 'auto' : '100vh', 
          overflow: hasResults ? 'visible' : 'hidden',
          overflowY: hasResults ? 'auto' : 'hidden',
          background: '#F5F6FA' 
        }}>
          {/* Sticky Header - Only show when NOT in review mode from props (test details page) */}
          {!isReviewModeFromProps && (
            <IELTSHeader
              timerText={
                hasResults
                  ? `Test Completed - ${timer.minutes !== 'Unlimited' ? formatTime(timer.minutes * 60 + timer.seconds) : 'Unlimited'}`
                  : isPracticeMode
                    ? `${formatClockTime(timer.minutes * 60 + timer.seconds)} elapsed`
                    : `${formatTime(timer.minutes * 60 + timer.seconds)} remaining`
              }
              showReview={!hasResults}
              showSaveExit={showSaveExit && !apiResult}
              showSubmit={true}
              isSubmitting={loading}
              apiResult={hasResults ? apiResult : null}
              groupId={groupId}
              onReview={() => {}}
              onSaveExit={handleSaveAndExit}
              onSubmit={handleSubmit}
              onSubmitNavigate={hasResults && apiResult?.response?.attemptId ? `/ielts-test-details?type=individual&attemptId=${apiResult.response.attemptId}` : null}
              submitButtonText={hasResults ? 'View Results' : undefined}
              submitButtonDisabled={loading}
              onFullTestPause={!hasResults ? onFullTestPause : undefined}
            />
          )}

          {/* Info Message Display */}
          {infoMessage && (
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              borderRadius: '8px', 
              backgroundColor: infoMessage.severity === 'error' ? '#FFEBEE' : infoMessage.severity === 'warning' ? '#FFF3E0' : infoMessage.severity === 'success' ? '#E8F5E9' : '#E3F2FD',
              border: `1px solid ${infoMessage.severity === 'error' ? '#E53935' : infoMessage.severity === 'warning' ? '#FF9800' : infoMessage.severity === 'success' ? '#4CAF50' : '#2196F3'}`,
              mx: 2
            }}>
              <Typography variant="body1" sx={{ 
                color: infoMessage.severity === 'error' ? '#C62828' : infoMessage.severity === 'warning' ? '#F57C00' : infoMessage.severity === 'success' ? '#2E7D32' : '#1976D2', 
                fontWeight: 500 
              }}>
                {infoMessage.text}
              </Typography>
            </Box>
          )}

          <SubscriptionAiPromptDialog
            open={subscriptionPromptOpen}
            onClose={() => setSubscriptionPromptOpen(false)}
            skillName="Speaking"
          />

          {/* Show results when apiResult is available and has scores */}
          {hasResults && apiResult && (() => {
            const scoreAvailable = shouldShowScore(apiResult);
            const aiScoring = getAIScoring(apiResult);
            const hasAI = hasAIScore(apiResult);
            
            if (!scoreAvailable && !hasAI) return null;
            
            return (
              <Box sx={{ mx: 2, mb: 2 }}>
                {scoreAvailable && <IELTSBandScore apiResult={apiResult} showCorrectIncorrect={false} showOnlyWhenScoreAvailable={true} preferManualScore={preferManualScore} />}
                {hasAI && <IELTSDetailedScoring aiScoring={aiScoring} testType="Speaking" />}
              </Box>
            );
          })()}

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: '77px', background: '#F5F6FA' }}>
        <MuiContainer maxWidth="md" sx={{ pt: 4, pb: 4 }}>
          {/* Part Header */}
          <PartHeader>
            <Typography variant="body1" sx={{ fontWeight: 700, color: '#2D3748' }}>
              {getFormattedPartName()}
            </Typography>
            {isThinking && (
              <ThinkTimerButton>
                Time to Think {thinkTime < 10 ? `00:0${thinkTime}` : `00:${thinkTime}`}
              </ThinkTimerButton>
            )}
          </PartHeader>

          {/* Question Card */}
          <QuestionBox>
            {/* Question Number Label */}
            <QuestionLabel>
              Question {currentQuestion?.order_id || (currentIdx + 1)}
            </QuestionLabel>
            
            {/* Display cue card if present */}
            {currentQuestion?.cue_card_text && (
              <Box sx={{ mb: 3, p: 3, backgroundColor: '#FFF9E6', borderRadius: 2, border: '2px solid #FFD700', textAlign: 'left', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '100%', overflow: 'hidden' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#996CFE' }}>Cue Card:</Typography>
                <Typography variant="body1" sx={{ color: '#2D3748', lineHeight: 1.6, wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '100%' }}>{currentQuestion.cue_card_text}</Typography>
              </Box>
            )}
            
            {/* Display statement if present */}
            {currentQuestion?.statement && (
              <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic', color: '#4A5568', fontSize: '18px', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '100%' }}>
                {currentQuestion.statement}
              </Typography>
            )}
            
            {/* Display the actual question */}
            <QuestionText>
              <span 
                ref={questionHtmlRef} 
                style={{ 
                  wordWrap: 'break-word', 
                  overflowWrap: 'break-word', 
                  wordBreak: 'break-word', 
                  maxWidth: '100%',
                  display: 'block'
                }} 
                dangerouslySetInnerHTML={{ __html: processSpeakingQuestionHtml(currentQuestion?.question_html || 'No question') }} 
              />
            </QuestionText>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minHeight: '200px', justifyContent: 'center' }}>
              {audioURLs[currentIdx] || audioBlobs[currentIdx] ? (
                <>
                  <MicrophoneButton playing={playing} onClick={handlePlayAudio} disabled={loading || recording}>
                    {playing ? <StopIcon /> : <PlayArrowIcon />}
                  </MicrophoneButton>
                  
                  {playing && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ width: 3, height: 8, bgcolor: '#996CFE', borderRadius: 1.5, animation: 'audioPulse 1s ease-in-out infinite' }} />
                      <Box sx={{ width: 3, height: 12, bgcolor: '#996CFE', borderRadius: 1.5, animation: 'audioPulse 1s ease-in-out infinite 0.2s' }} />
                      <Box sx={{ width: 3, height: 10, bgcolor: '#996CFE', borderRadius: 1.5, animation: 'audioPulse 1s ease-in-out infinite 0.4s' }} />
                      <Box sx={{ width: 3, height: 14, bgcolor: '#996CFE', borderRadius: 1.5, animation: 'audioPulse 1s ease-in-out infinite 0.6s' }} />
                      <Box sx={{ width: 3, height: 10, bgcolor: '#996CFE', borderRadius: 1.5, animation: 'audioPulse 1s ease-in-out infinite 0.4s' }} />
                      <Box sx={{ width: 3, height: 12, bgcolor: '#996CFE', borderRadius: 1.5, animation: 'audioPulse 1s ease-in-out infinite 0.2s' }} />
                      <Box sx={{ width: 3, height: 8, bgcolor: '#996CFE', borderRadius: 1.5, animation: 'audioPulse 1s ease-in-out infinite' }} />
                    </Box>
                  )}
                  
                  <Typography variant="body2" sx={{ color: playing ? '#996CFE' : '#37965E', fontWeight: 600, fontSize: '14px' }}>
                    {playing ? 'Click to stop playing' : 'Click to play'}
                  </Typography>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      if (playing && audioRef.current) {
                        audioRef.current.pause();
                        audioRef.current = null;
                        setPlaying(false);
                      }
                      skipThinkingRef.current = true;
                      setIsThinking(false);
                      setThinkTime(0);
                      setAudioURLs(prev => {
                        const newUrls = { ...prev };
                        delete newUrls[currentIdx];
                        return newUrls;
                      });
                      setAudioBlobs(prev => {
                        const newBlobs = { ...prev };
                        delete newBlobs[currentIdx];
                        return newBlobs;
                      });
                      await handleStartRecording();
                    }}
                    disabled={loading || recording}
                    sx={{
                      borderColor: '#996CFE',
                      color: '#996CFE',
                      fontSize: '13px',
                      padding: '6px 16px',
                      '&:hover': {
                        borderColor: '#7C4DFF',
                        backgroundColor: 'rgba(153, 108, 254, 0.08)',
                      },
                    }}
                  >
                    Record Again
                  </Button>
                </>
              ) : recording ? (
                <>
                  <MicrophoneButton recording onClick={handleStopRecording} disabled={loading}>
                    <StopIcon />
                  </MicrophoneButton>
                  <Typography variant="body2" sx={{ color: '#E53935', fontWeight: 600, fontSize: '14px' }}>
                    Click to stop recording
                  </Typography>
                </>
              ) : (
                <>
                  <MicrophoneButton thinking={isThinking} onClick={handleStartRecording} disabled={loading}>
                    <MicIcon />
                  </MicrophoneButton>
                  <Typography variant="body2" sx={{ color: isThinking ? '#996CFE' : '#4A5568', fontWeight: 500, fontSize: '14px' }}>
                    {isThinking ? 'Get ready to speak... (or click to start recording now)' : 'Click to record your answer'}
                  </Typography>
                </>
              )}
              {loading && (
                <CircularProgress size={24} sx={{ color: '#996CFE', mt: 1 }} />
              )}
            </Box>
          </QuestionBox>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <Button 
              onClick={handleBack} 
              disabled={currentIdx === 0 || loading || recording} 
              variant="outlined"
              sx={{
                borderColor: '#E0E0E0',
                color: '#4A5568',
                '&:hover': {
                  borderColor: '#996CFE',
                  backgroundColor: 'rgba(153, 108, 254, 0.08)',
                },
                '&:disabled': {
                  borderColor: '#E0E0E0',
                  color: '#A0AEC0',
                },
              }}
            >
              Previous
            </Button>
            
            {currentIdx < allQuestions.length - 1 && (
              <Button 
                onClick={handleNext} 
                variant="contained" 
                disabled={loading || recording}
                sx={{
                  backgroundColor: '#996CFE',
                  '&:hover': {
                    backgroundColor: '#7C4DFF',
                  },
                  '&:disabled': {
                    backgroundColor: '#E0E0E0',
                    color: '#A0AEC0',
                  },
                }}
              >
                Next
              </Button>
            )}
          </Box>
        </MuiContainer>
          </Box>
          
          {/* Bottom Navigation */}
          <IELTSBottomNavigation
            parts={sections.map((section, idx) => ({
              part: idx + 1,
              sectionIdx: idx,
              part_name: section.part_name || `Part ${idx + 1}`
            }))}
            currentPart={currentQuestion?.sectionIdx !== undefined ? currentQuestion.sectionIdx + 1 : 1}
            onPartChange={handlePartChange}
            getQuestionNumbers={getSectionQuestionNumbers}
            isQuestionAnswered={isQuestionAnswered}
            allAnswers={getAnsweredQuestions()}
            skippedQuestions={[]}
            onQuestionClick={handleQuestionClick}
            getPartName={(part) => part.part_name || `Part ${part.part || part}`}
            disabled={recording}
            visitedParts={visitedParts}
            lockUnvisitedParts={true}
          />
        </Box>
      )}
    </>
  );
};

export default SpeakingTestUI;
